<script setup lang="ts">
import { AlignHorizontalCenters, AlignLeftBox, AlignRightBox } from '@iconoir/vue'
import type { Component } from 'vue'

import EmailLinkInput from '../ui/EmailLinkInput.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'
import EmailPanelSection from '../EmailPanelSection.vue'

import type { Alignment, ButtonBlock } from '../../types'

const { t } = useI18n()

const props = defineProps<{
  block: ButtonBlock
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:block', block: ButtonBlock): void
}>()

const updateField = <K extends keyof ButtonBlock>(field: K, value: ButtonBlock[K]) => {
  emit('update:block', { ...props.block, [field]: value })
}

const alignments: { value: Alignment; icon: Component }[] = [
  { value: 'left', icon: AlignLeftBox },
  { value: 'center', icon: AlignHorizontalCenters },
  { value: 'right', icon: AlignRightBox }
]
</script>

<template>
  <Teleport to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="block.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => updateField('blockBackgroundColor', v)"
        />
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.globalAlignment')">
        <Tabs :model-value="block.align" @update:model-value="(v) => updateField('align', v as Alignment)">
          <TabsList class="w-full">
            <TabsTrigger v-for="opt in alignments" :key="opt.value" :value="opt.value" class="flex-1">
              <component :is="opt.icon" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.color')">
        <div class="space-y-3">
          <div class="space-y-1.5">
            <Label class="text-xs font-medium text-foreground">
              {{ t('pages.email.emailTemplateCreatePage.panel.background') }}
            </Label>
            <ColorPickerWithPresets
              :model-value="block.backgroundColor"
              :presets="colorPresets ?? []"
              @update:model-value="(v) => updateField('backgroundColor', v)"
            />
          </div>
          <div class="space-y-1.5">
            <Label class="text-xs font-medium text-foreground">
              {{ t('pages.email.emailTemplateCreatePage.panel.text') }}
            </Label>
            <ColorPickerWithPresets
              :model-value="block.textColor"
              :presets="colorPresets ?? []"
              @update:model-value="(v) => updateField('textColor', v)"
            />
          </div>
        </div>
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.link')">
        <EmailLinkInput
          stacked
          class="w-full"
          :model-value="block.url"
          @update:model-value="(v) => updateField('url', v)"
        />
      </EmailPanelSection>
    </div>
  </Teleport>
</template>
